/* retailers.js — v2 (Storefront API, Cursor-Pagination, vollständiges DOM-Rendering) */

(() => {
  // ---- Config / Globals ----------------------------------------------------
  const SF_ENDPOINT = window.SF_ENDPOINT || "/api/2025-07/graphql.json";
  const SF_TOKEN    = window.SF_TOKEN;

  const q  = (s, r=document) => r.querySelector(s);
  const qa = (s, r=document) => Array.from(r.querySelectorAll(s));

  const ROOT_ID       = "#retailer-root";
  const NAV_ID        = "#continent-nav";
  const BRAND_SEL_ID  = "#filter-brand";
  const COUNTRY_SEL_ID= "#filter-country";
  const SEARCH_ID     = "#retailer-search";
  const WORLD_MAP_SEARCH_ID = "#world-map-search";
  const CLEAR_BTN_ID  = "#filters-clear";

  // Ziel-Elemente (werden später befüllt)
  const root        = q(ROOT_ID);
  const nav         = q(NAV_ID);
  const brandSelect = q(BRAND_SEL_ID);
  const countrySel  = q(COUNTRY_SEL_ID);
  const searchInput = q(SEARCH_ID);
  const worldMapSearch = q(WORLD_MAP_SEARCH_ID);
  const clearBtn    = q(CLEAR_BTN_ID);

  // Optionale SVG-Interaktion (wenn map vorhanden)
  const continentPaths = qa(".continent-path");

  // Continent-Sortierung
  const CONT_ORDER = [
    "Europe","Asia","North America","South America","Africa","Oceania","Middle East","Central America","Other"
  ];

  // ---- Boot Guard ----------------------------------------------------------
  document.addEventListener("DOMContentLoaded", init);

  async function init() {
    if (!root) return console.error("Retailer root not found:", ROOT_ID);
    if (!SF_TOKEN) {
      console.error("Storefront token fehlt – prüfe theme.liquid & Theme-Einstellungen.");
      return;
    }

    const all = await fetchAllRetailers();
    if (!all.length) {
      root.innerHTML = `<p class="empty-state">Keine Händler gefunden.</p>`;
      return;
    }

    // Optionen / UI-Bausteine befüllen
    buildUI(all);
    // Rendering
    renderAll(all);
    // Empty-State Box vorbereiten
    ensureEmptyBox();
    // Filter aktivieren
    wireFilters();
    // SVG-Map optional anbinden
    wireSVGMap();
    // IntersectionObserver für aktive Kontinent-Links
    wireActiveContinent();
    // Initial Filter
    applyFilters();
  }

  // ---- Data: Storefront GraphQL -------------------------------------------
  const GQL = `
  query Retailers($first:Int!, $after:String) {
    metaobjects(type:"retailer", first:$first, after:$after) {
      edges {
        node {
          id
          handle
          fields { key value }
        }
      }
      pageInfo { hasNextPage endCursor }
    }
  }`;

  async function fetchAllRetailers() {
    let after = null, all = [];
    do {
      const res = await fetch(SF_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": SF_TOKEN
        },
        body: JSON.stringify({ query: GQL, variables: { first: 250, after } })
      });
      const json = await res.json();
      if (json.errors) {
        console.error("GraphQL errors:", json.errors); break;
      }
      const block = json.data?.metaobjects;
      if (!block) break;
      all.push(...block.edges.map(e => normalize(e.node)));
      after = block.pageInfo.hasNextPage ? block.pageInfo.endCursor : null;
    } while (after);

    // Nur aktive
    return all.filter(r => r.is_active === true);
  }

  function normalize(node) {
    const obj = { id: node.id, handle: node.handle };
    for (const f of node.fields) obj[f.key] = f.value;

    // Strings normalisieren
    const toStr = v => (v == null ? "" : String(v));

    const brandRaw = toStr(obj.brands || obj.brand);
    return {
      id: node.id,
      handle: node.handle,
      title: toStr(obj.title || obj.name),
      brands: brandRaw,
      brand: brandRaw, // Alias
      is_active: String(obj.is_active).toLowerCase() === "true",
      continent: toStr(obj.continent) || "Other",
      country: toStr(obj.country),
      city: toStr(obj.city),
      state: toStr(obj.state),
      postal_code: toStr(obj.postal_code),
      address: toStr(obj.address),
      phone: toStr(obj.phone),
      email: toStr(obj.email),
      website: toStr(obj.website),
      // vorgefertigter Suchtext
      _search: [
        toStr(obj.title||obj.name), obj.city, obj.state, obj.country,
        obj.address, obj.postal_code, brandRaw
      ].filter(Boolean).join(" ").toLowerCase()
    };
  }

  // ---- UI Build ------------------------------------------------------------
  function buildUI(all) {
    // Brand-/Länder-Optionen
    const countries = new Set();
    const brands    = new Set();

    all.forEach(r => {
      if (r.country) countries.add(r.country);
      if (r.brand)   brands.add(r.brand);
    });

    // Länder
    if (countrySel) {
      countrySel.innerHTML = `<option value="">Alle Länder</option>` +
        [...countries].sort((a,b)=>a.localeCompare(b))
        .map(c => `<option value="${escAttr(c)}">${esc(c)}</option>`).join("");
    }

    // Marken
    if (brandSelect) {
      brandSelect.innerHTML = `<option value="">Alle Sportarten</option>` +
        [...brands].sort((a,b)=>a.localeCompare(b))
        .map(b => `<option value="${escAttr(b.toLowerCase())}">${esc(b)}</option>`).join("");
    }

    // Kontinent-Navigation
    if (nav) {
      const groups = groupBy(all, r => r.continent || "Other");
      const keys   = [...groups.keys()].sort(sortCont);
      nav.innerHTML = keys.map(cont => {
        const id = sectionId(cont);
        return `<a href="#${escAttr(id)}" class="continent-link" data-continent="${escAttr(id)}">${esc(cont)}</a>`;
      }).join("");
    }
  }

  // ---- Render --------------------------------------------------------------
  function renderAll(all) {
    const groups = groupBy(all, r => r.continent || "Other");
    const continents = [...groups.keys()].sort(sortCont);

    root.innerHTML = "";
    continents.forEach(cont => {
      const id   = sectionId(cont);
      const list = groups.get(cont).sort((a,b) => a.country.localeCompare(b.country) || a.title.localeCompare(b.title));

      const sec = document.createElement("section");
      sec.className = "retailer-group";
      sec.dataset.group = cont;
      sec.innerHTML = `
        <h2 id="${escAttr(id)}" class="continent-heading">${esc(cont)}</h2>
        <div class="retailer-grid"></div>
      `;
      const grid = q(".retailer-grid", sec);
      list.forEach(r => grid.insertAdjacentHTML("beforeend", cardHTML(r, cont)));
      root.appendChild(sec);
    });

    // Empty-State Box
    ensureEmptyBox();
  }

  function cardHTML(r, continent) {
    const addrBits = [r.address, r.postal_code, r.city, r.state, r.country].filter(Boolean).join(", ");
    const website  = r.website ? `<a href="${escAttr(r.website)}" target="_blank" rel="noopener" class="contact-link">
      <svg focusable="false" width="16" height="16" class="icon icon--picto-worldwide" viewBox="0 0 24 24">
        <path d="M12 2C15.2712 2 18.1755 3.57069 20 5.99902M12 2L13 6L15 8L17.5 7.5L20 5.99902M12 2C9.51846 2 7.24803 2.9039 5.5 4.40041M20 5.99902C21.2558 7.67051 22 9.74835 22 12C22 17.5228 17.5228 22 12 22C9.27455 22 6.80375 20.9097 5 19.1414M5.5 4.40041C3.35767 6.2345 2 8.9587 2 12C2 14.7974 3.14864 17.3265 5 19.1414M5.5 4.40041L9 7.5L8.5 10L5 12L6.5 14L7.5 16L6 17.5L5 19.1414M15 11L17 11.5L18 13L16 17L14 18L12 17L12.5 15L12 13L13.5 11.5L15 11Z" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path>
      </svg>
      ${esc(r.website.replace(/^https?:\/\//, ''))}
    </a>` : "";
    const phone    = r.phone ? `<a href="tel:${escAttr(r.phone)}" class="contact-link">
      <svg focusable="false" width="16" height="16" class="icon icon--picto-phone" viewBox="0 0 24 24">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M20.6636 16.7325L17.6844 13.7366C17.2337 13.2827 16.4999 13.2827 16.048 13.7343L13.4005 16.3802L7.62246 10.6056L10.2734 7.95613C10.7241 7.5057 10.7253 6.77463 10.2746 6.32305L7.29311 3.33869C6.84126 2.8871 6.10976 2.8871 5.65791 3.33869L3.00462 5.98927L3 5.9858C3 14.2783 9.72568 21 18.023 21L20.6613 18.3633C21.1119 17.9129 21.1131 17.1841 20.6636 16.7325Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
      </svg>
      ${esc(r.phone.replace(/^tel:/, ''))}
    </a>` : "";
    const email    = r.email ? `<a href="mailto:${escAttr(r.email)}" class="contact-link">
      <svg focusable="false" width="16" height="16" class="icon icon--picto-email" viewBox="0 0 24 24">
        <path d="M21 8V5H3V8M21 8V19H3V8M21 8L12 12.5L3 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
      </svg>
      ${esc(r.email.replace(/^mailto:/, ''))}
    </a>` : "";

    return `
    <article class="retailer-card"
      data-continent="${escAttr(continent)}"
      data-country="${escAttr(r.country)}"
      data-brand="${escAttr((r.brand||"").toLowerCase())}"
      data-text="${escAttr(r._search)}">
      <div class="retailer-card__body">
        <div class="retailer-card__header">
          <div class="retailer-card__title">${esc(r.title || "Unnamed Retailer")}</div>
          ${r.country ? `<div class="retailer-card__badge country-badge">${esc(r.country)}</div>` : ""}
        </div>
        <div class="retailer-card__meta">
          ${r.city ? `<span>${esc(r.city)}</span>` : ""}
          ${r.brand ? `${r.city ? ` · ` : ""}<span>${esc(r.brand)}</span>` : ""}
        </div>
        ${addrBits ? `<div class="retailer-card__address">${esc(addrBits.split(', ').filter(bit => bit !== r.country).join(', '))}</div>` : ""}
        <div class="retailer-card__links">${website} ${phone} ${email}</div>
      </div>
    </article>`;
  }

  // ---- Filters -------------------------------------------------------------
  function wireFilters() {
    brandSelect  && brandSelect.addEventListener("change", applyFilters);
    countrySel   && countrySel.addEventListener("change", applyFilters);
    searchInput  && searchInput.addEventListener("input", debounce(applyFilters, 150));
    worldMapSearch && worldMapSearch.addEventListener("input", debounce(applyFilters, 150));
    
    // Synchronisiere beide Suchfelder
    if (searchInput && worldMapSearch) {
      searchInput.addEventListener("input", () => {
        worldMapSearch.value = searchInput.value;
      });
      worldMapSearch.addEventListener("input", () => {
        searchInput.value = worldMapSearch.value;
      });
    }
    clearBtn     && clearBtn.addEventListener("click", resetFilters);

    // Nav Scroll
    qa(".continent-link", nav).forEach(link => {
      link.addEventListener("click", ev => {
        ev.preventDefault();
        const targetId = link.getAttribute("href").slice(1);
        const el = q(`#${CSS.escape(targetId)}`);
        if (!el) return;
        const headerHeight = 120, navHeight = 60, totalOffset = headerHeight + navHeight + 20;
        const pos = el.offsetTop - totalOffset;
        window.scrollTo({ top: pos, behavior: "smooth" });
      });
    });
  }

  function applyFilters() {
    const selBrand   = (brandSelect?.value || "").toLowerCase();
    const selCountry = (countrySel?.value || "");
    const term       = (searchInput?.value || worldMapSearch?.value || "").toLowerCase().trim();
  
    console.log("Filter angewendet:", { selBrand, selCountry, term });
  
    const cards = qa(".retailer-card", root);
    console.log("Gefundene Karten:", cards.length);
    let visibleCount = 0;
  
    cards.forEach(card => {
      // Daten robust lesen
      const cBrand   = (card.dataset.brand || "");                   // bereits lowercase
      const cCountry = (card.dataset.country || "");
      const text     = (card.dataset.text || card.textContent || "").toLowerCase();
  
      // Match-Regeln
      const okBrand   = !selBrand   || cBrand === selBrand || cBrand.includes(selBrand);
      const okCountry = !selCountry || cCountry === selCountry;
      const okTerm    = !term       || text.includes(term);
  
      const show = okBrand && okCountry && okTerm;
      card.classList.toggle("is-hidden", !show);
      if (show) visibleCount++;
    });
  
    // Gruppen ohne Treffer einklappen
    qa(".retailer-group", root).forEach(g => {
      const anyVisible = qa(".retailer-card:not(.is-hidden)", g).length > 0;
      g.classList.toggle("is-empty", !anyVisible);
    });
  
    // Empty-State anzeigen/verstecken
    if (emptyBox) {
      if (visibleCount === 0) {
        emptyBox.style.display = "block";
        emptyBox.classList.remove('is-hidden');
      } else {
        emptyBox.style.display = "none";
        emptyBox.classList.add('is-hidden');
      }
    }

  }
  

  // ---- Active Continent (IO) ----------------------------------------------
  function wireActiveContinent() {
    const headings = qa(".continent-heading", root);
    if (!headings.length || !nav) return;

    const links = qa(".continent-link", nav);
    const setActive = (id) => {
      links.forEach(a => { a.classList.remove("is-active"); a.setAttribute("aria-current","false"); });
      const el = q(`a[href="#${CSS.escape(id)}"]`, nav);
      if (el) { el.classList.add("is-active"); el.setAttribute("aria-current","page"); }
    };

    const io = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) setActive(e.target.id); });
    }, { rootMargin: "-30% 0px -50% 0px", threshold: [0,0.25,0.5,0.75,1] });

    headings.forEach(h => io.observe(h));
  }

  // ---- SVG Map Wiring (optional) ------------------------------------------
  function wireSVGMap() {
    if (!continentPaths.length) return;
    console.log("Weltkarte-Pfade gefunden:", continentPaths.length);
    
    continentPaths.forEach(path => {
      console.log("Pfad ID:", path.id);
      
      path.addEventListener("click", (e) => {
        e.preventDefault();
        const svgId = path.id; // z.B. "Europe"
        console.log("Klick auf Kontinent:", svgId);
        
        // Mapping für verschiedene Kontinent-Namen
        const continentMapping = {
          'Europe': 'Europe',
          'Asia': 'Asia', 
          'North_America': 'North America',
          'South_America': 'South America',
          'Africa': 'Africa',
          'Oceania': 'Oceania',
          'Middle_East': 'Middle East',
          'Central_America': 'Central America'
        };
        
        // Debug: Alle verfügbaren Sektionen anzeigen
        const allSections = qa('.continent-heading', root);
        console.log("Verfügbare Sektionen:", allSections.map(s => ({ id: s.id, text: s.textContent })));
        
        const mappedContinent = continentMapping[svgId] || svgId;
        console.log("Gemappter Kontinent:", mappedContinent);
        
        // Suche nach dem Ziel mit verschiedenen Strategien
        let target = q(`#${CSS.escape(sectionId(mappedContinent))}`);
        
        if (!target) {
          // Versuche es mit dem deutschen Namen
          const germanMapping = {
            'Europe': 'Europa',
            'Asia': 'Asien',
            'North America': 'Nordamerika',
            'South America': 'Südamerika',
            'Africa': 'Afrika',
            'Oceania': 'Ozeanien',
            'Middle East': 'Naher Osten',
            'Central America': 'Mittelamerika'
          };
          
          const germanName = germanMapping[mappedContinent];
          if (germanName) {
            target = q(`#${CSS.escape(sectionId(germanName))}`);
            console.log("Versuche deutschen Namen:", germanName);
          }
        }
        
        if (!target) {
          // Suche nach dem Text-Inhalt
          const textTarget = Array.from(allSections).find(h => 
            h.textContent.toLowerCase().includes(mappedContinent.toLowerCase()) ||
            h.textContent.toLowerCase().includes(svgId.toLowerCase())
          );
          
          if (textTarget) {
            target = textTarget;
            console.log("Gefunden über Text-Suche:", textTarget.textContent);
          }
        }
        
        if (!target) {
          console.log("Ziel nicht gefunden für:", mappedContinent, "ID:", sectionId(mappedContinent));
          console.log("Verfügbare Sektionen:", allSections.map(s => s.textContent));
          return;
        }
        
        console.log("Scrolle zu:", target.id || target.textContent);
        const headerHeight = 120, navHeight = 60, totalOffset = headerHeight + navHeight + 20;
        window.scrollTo({ top: target.offsetTop - totalOffset, behavior: "smooth" });
      });
      
      // Hover-Effekt für bessere UX
      path.addEventListener("mouseenter", () => {
        path.style.cursor = "pointer";
        path.style.opacity = "0.8";
      });
      
      path.addEventListener("mouseleave", () => {
        path.style.cursor = "default";
        path.style.opacity = "1";
      });
    });
  }

  // ---- Helpers -------------------------------------------------------------
  function groupBy(arr, fn) {
    const m = new Map();
    for (const it of arr) {
      const key = fn(it);
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(it);
    }
    return m;
  }

  function sortCont(a, b) {
    const ia = CONT_ORDER.indexOf(a); const ib = CONT_ORDER.indexOf(b);
    const sa = ia < 0 ? 999 : ia; const sb = ib < 0 ? 999 : ib;
    return sa - sb || a.localeCompare(b);
    }

  function sectionId(continent) {
    // konsistente IDs: "cont-europe" etc.
    return `cont-${handleize(continent)}`;
  }

  function handleize(s) {
    return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[m]));
  }

  function escAttr(s) { return esc(s); }

  function debounce(fn, ms=150) {
    let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  }

  // Empty state node einmalig anlegen
  let emptyBox = null;

  function ensureEmptyBox() {
    if (!emptyBox) {
      emptyBox = document.createElement("div");
      emptyBox.className = "empty-state";
      emptyBox.innerHTML = `
        <div class="empty-state__content">
          <h3>Keine Händler gefunden</h3>
          <p>Versuche andere Filter-Einstellungen oder lösche alle Filter.</p>
          <button class="btn btn--secondary" onclick="resetFilters()">Filter zurücksetzen</button>
        </div>
      `;
      emptyBox.style.display = "none";
      root.appendChild(emptyBox);
    }
  }

  function resetFilters() {
    if (brandSelect) brandSelect.value = "";
    if (countrySel)  countrySel.value  = "";
    if (searchInput) searchInput.value = "";
    if (worldMapSearch) worldMapSearch.value = "";
    applyFilters();
  }

  // Globale Funktion für HTML onclick
  window.resetFilters = resetFilters;

})();
